import { useState, useEffect } from 'react';
import API from '../api/client';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, Tooltip,
  XAxis, YAxis, CartesianGrid, ResponsiveContainer, Legend, AreaChart, Area
} from 'recharts';
import { Activity, Users, Calendar, TrendingUp, Heart, AlertTriangle, Scale } from 'lucide-react';

const COLORS_MAIN = ['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#6366f1'];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-stone-200 rounded-xl shadow-lg px-4 py-3 text-sm">
      {label && <p className="font-semibold text-stone-700 mb-1">{label}</p>}
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>{p.name}: <b>{p.value?.toLocaleString('fr-FR')}</b></p>
      ))}
    </div>
  );
};

function StatCard({ icon: Icon, label, value, sub, color }) {
  const colorMap = {
    emerald: 'bg-emerald-50 text-emerald-600 border-emerald-200',
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    amber: 'bg-amber-50 text-amber-600 border-amber-200',
    red: 'bg-red-50 text-red-600 border-red-200',
  };
  return (
    <div className="bg-white border border-stone-200 rounded-xl p-5">
      <div className={`w-10 h-10 rounded-lg border flex items-center justify-center mb-3 ${colorMap[color] || colorMap.emerald}`}>
        <Icon size={20} />
      </div>
      <p className="text-2xl font-heading font-bold text-stone-900">{typeof value === 'number' ? value.toLocaleString('fr-FR') : value}</p>
      <p className="text-sm text-stone-600 font-medium mt-0.5">{label}</p>
      {sub && <p className="text-xs text-stone-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function ChartCard({ title, children, className = '' }) {
  return (
    <div className={`bg-white border border-stone-200 rounded-xl p-5 ${className}`}>
      <h3 className="font-heading font-semibold text-stone-800 mb-4 text-sm">{title}</h3>
      {children}
    </div>
  );
}

export default function StatsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get('/stats/advanced').then(r => {
      setData(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
    </div>
  );

  if (!data) return (
    <div className="text-center py-16 text-stone-400">
      <TrendingUp size={40} className="text-stone-200 mx-auto mb-3" />
      <p>Impossible de charger les statistiques</p>
    </div>
  );

  const { monthly, by_city, age_distribution, sex_distribution, appointment_status, vitals, weekly_activity } = data;

  const hta_pct = vitals.imc_renseigne > 0 ? Math.round((vitals.patients_hta / vitals.imc_renseigne) * 100) : 0;
  const obesite_pct = vitals.imc_renseigne > 0 ? Math.round((vitals.patients_obesite / vitals.imc_renseigne) * 100) : 0;

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="stats-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-stone-900">Statistiques</h1>
        <p className="text-stone-500 text-sm mt-0.5">Analyse médicale globale de votre patientèle</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={TrendingUp} label="TA systolique moyenne" value={`${vitals.ta_systolique_moy || '—'} mmHg`} color="red" />
        <StatCard icon={Heart} label="Pouls moyen" value={`${vitals.pouls_moyen || '—'} bpm`} color="emerald" />
        <StatCard icon={Scale} label="IMC moyen" value={`${vitals.imc_moyen || '—'}`} color="amber" />
        <StatCard icon={AlertTriangle} label="Patients HTA détectée" value={vitals.patients_hta} sub={`${hta_pct}% des dossiers avec TA`} color="red" />
      </div>

      {/* Monthly Trend */}
      <ChartCard title="Activité mensuelle — Consultations & Rendez-vous (12 derniers mois)" className="col-span-full">
        {monthly.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={monthly} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <defs>
                <linearGradient id="cgCons" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="cgRdv" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="mois" tick={{ fontSize: 11, fill: '#94a3b8' }} />
              <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="consultations" name="Consultations" stroke="#10b981" fill="url(#cgCons)" strokeWidth={2} dot={{ r: 3 }} />
              <Area type="monotone" dataKey="rdv" name="Rendez-vous" stroke="#3b82f6" fill="url(#cgRdv)" strokeWidth={2} dot={{ r: 3 }} />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-40 flex items-center justify-center text-stone-400 text-sm">Données insuffisantes</div>
        )}
      </ChartCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* By City */}
        <ChartCard title="Répartition géographique — Top 10 villes">
          {by_city.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={by_city} layout="vertical" margin={{ top: 0, right: 20, left: 80, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                <YAxis type="category" dataKey="ville" tick={{ fontSize: 11, fill: '#64748b' }} width={80} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="patients" name="Patients" radius={[0, 4, 4, 0]}>
                  {by_city.map((_, i) => <Cell key={i} fill={COLORS_MAIN[i % COLORS_MAIN.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-40 flex items-center justify-center text-stone-400 text-sm">Aucune donnée de ville</div>
          )}
        </ChartCard>

        {/* Age Distribution */}
        <ChartCard title="Pyramide des âges">
          {age_distribution.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={age_distribution} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="tranche" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="patients" name="Patients" radius={[4, 4, 0, 0]}>
                  {age_distribution.map((_, i) => <Cell key={i} fill={COLORS_MAIN[i % COLORS_MAIN.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-40 flex items-center justify-center text-stone-400 text-sm">Aucune donnée d'âge</div>
          )}
        </ChartCard>

        {/* Sex Distribution */}
        <ChartCard title="Répartition par genre">
          {sex_distribution.length > 0 ? (
            <div className="flex items-center gap-6">
              <ResponsiveContainer width="60%" height={220}>
                <PieChart>
                  <Pie data={sex_distribution} cx="50%" cy="50%" innerRadius={60} outerRadius={90}
                    dataKey="value" nameKey="sexe" paddingAngle={4}>
                    {sex_distribution.map((entry, i) => (
                      <Cell key={i} fill={entry.fill || COLORS_MAIN[i]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(val) => val.toLocaleString('fr-FR')} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-3">
                {sex_distribution.map((d, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: d.fill || COLORS_MAIN[i] }} />
                    <div>
                      <p className="text-sm font-medium text-stone-700">{d.sexe}</p>
                      <p className="text-xs text-stone-500">{d.value?.toLocaleString('fr-FR')} patients</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-40 flex items-center justify-center text-stone-400 text-sm">Aucune donnée</div>
          )}
        </ChartCard>

        {/* Appointment Status */}
        <ChartCard title="Statut des rendez-vous">
          {appointment_status.length > 0 ? (
            <div className="flex items-center gap-6">
              <ResponsiveContainer width="60%" height={220}>
                <PieChart>
                  <Pie data={appointment_status} cx="50%" cy="50%" innerRadius={60} outerRadius={90}
                    dataKey="value" nameKey="statut" paddingAngle={4}>
                    {appointment_status.map((entry, i) => (
                      <Cell key={i} fill={entry.fill || COLORS_MAIN[i]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(val) => val.toLocaleString('fr-FR')} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-3">
                {appointment_status.map((d, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: d.fill || COLORS_MAIN[i] }} />
                    <div>
                      <p className="text-sm font-medium text-stone-700 capitalize">{d.statut}</p>
                      <p className="text-xs text-stone-500">{d.value?.toLocaleString('fr-FR')} RDV</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-40 flex items-center justify-center text-stone-400 text-sm">Aucune donnée</div>
          )}
        </ChartCard>
      </div>

      {/* Weekly activity */}
      <ChartCard title="Activité hebdomadaire — RDV par jour de la semaine (3 derniers mois)">
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={weekly_activity} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="jour" tick={{ fontSize: 12, fill: '#94a3b8' }} />
            <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="rdv" name="Rendez-vous" fill="#10b981" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Vitals indicators */}
      <div className="bg-white border border-stone-200 rounded-xl p-5">
        <h3 className="font-heading font-semibold text-stone-800 mb-4 text-sm">Indicateurs de Santé Publique</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'TA Systolique', value: `${vitals.ta_systolique_moy} mmHg`, normal: vitals.ta_systolique_moy < 130, normal_text: 'Normal < 130' },
            { label: 'TA Diastolique', value: `${vitals.ta_diastolique_moy} mmHg`, normal: vitals.ta_diastolique_moy < 80, normal_text: 'Normal < 80' },
            { label: 'IMC moyen', value: vitals.imc_moyen, normal: vitals.imc_moyen < 25, normal_text: 'Normal < 25' },
            { label: 'Obésité (IMC≥30)', value: `${obesite_pct}%`, normal: obesite_pct < 15, normal_text: 'Idéal < 15%' },
          ].map(({ label, value, normal, normal_text }) => (
            <div key={label} className={`rounded-xl p-4 border ${normal ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
              <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide">{label}</p>
              <p className={`text-2xl font-bold mt-1 ${normal ? 'text-emerald-700' : 'text-amber-700'}`}>{value}</p>
              <p className={`text-xs mt-1 ${normal ? 'text-emerald-600' : 'text-amber-600'}`}>{normal_text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
